import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from 'recharts'
import { Download, TrendingUp, Users, Euro, Building2, FileSpreadsheet, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'

const ANNO_CORRENTE = new Date().getFullYear()
const ANNI = Array.from({ length: 4 }, (_, i) => ANNO_CORRENTE - i)
const MESI = ['', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
const MESI_FULL = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']

function fmt(v: number | null | undefined) {
  if (v == null) return '—'
  return '€ ' + v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

function KpiCard({ label, value, icon: Icon, color }: {
  label: string; value: string; icon: any; color: string
}) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12,
      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
      flex: '1 1 140px', minWidth: 0,
    }}>
      <div style={{ background: color + '18', borderRadius: 10, padding: 9, flexShrink: 0 }}>
        <Icon size={20} color={color} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
        <div style={{ fontSize: 11, color: '#94A3B8' }}>{label}</div>
      </div>
    </div>
  )
}

function AccordionTable({ titolo, intestazioni, righe, bg = '#fff' }: {
  titolo: string; intestazioni: string[]; righe: (string | number | null)[][]; bg?: string
}) {
  const [aperto, setAperto] = useState(true)
  return (
    <div style={{ background: bg, border: '1px solid #E5E7EB', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
      <div
        onClick={() => setAperto(a => !a)}
        style={{
          padding: '11px 16px', fontWeight: 600, fontSize: 13, color: '#1E40AF',
          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', userSelect: 'none',
          background: '#EFF6FF',
        }}
      >
        <span>{titolo}</span>
        <span style={{ color: '#94A3B8' }}>{aperto ? '▲' : '▼'}</span>
      </div>
      {aperto && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {intestazioni.map(h => (
                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {righe.length === 0 ? (
                <tr><td colSpan={intestazioni.length} style={{ padding: '12px', color: '#94A3B8', textAlign: 'center' }}>
                  Nessun dato
                </td></tr>
              ) : righe.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                  {r.map((cell, j) => (
                    <td key={j} style={{ padding: '6px 10px', borderBottom: '1px solid #F1F5F9', color: '#1E293B', whiteSpace: 'nowrap' }}>
                      {cell ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function TrasferteReport() {
  const isMobile = useIsMobile()
  const [anno, setAnno] = useState<number>(ANNO_CORRENTE)
  const [mese, setMese] = useState<number>(0)
  const [commessa, setCommessa] = useState('')

  const params = {
    anno: anno || undefined,
    mese: mese || undefined,
    nr_commessa: commessa.trim() || undefined,
  }

  const { data: commesse = [], isLoading: lcm } = useQuery<any[]>({
    queryKey: ['trasferte-report-commesse', params],
    queryFn: () => api.get('/trasferte/report/commesse', { params }).then(r => r.data),
  })

  const { data: operatori = [] } = useQuery<any[]>({
    queryKey: ['trasferte-report-operatori', params],
    queryFn: () => api.get('/trasferte/report/operatori', { params }).then(r => r.data),
  })

  const { data: alberghi = [] } = useQuery<any[]>({
    queryKey: ['trasferte-report-alberghi', params],
    queryFn: () => api.get('/trasferte/report/alberghi', { params }).then(r => r.data),
  })

  const { data: timeline = [] } = useQuery<any[]>({
    queryKey: ['trasferte-report-timeline', { anno, nr_commessa: commessa || undefined }],
    queryFn: () => api.get('/trasferte/report/timeline', {
      params: { anno: anno || undefined, nr_commessa: commessa.trim() || undefined },
    }).then(r => r.data),
  })

  const totTrasf  = commesse.reduce((s: number, c: any) => s + (c.num_trasferte || 0), 0)
  const totCosto  = commesse.reduce((s: number, c: any) => s + parseFloat(c.totale_costo || 0), 0)
  const totDiaria = commesse.reduce((s: number, c: any) => s + parseFloat(c.totale_diaria || 0), 0)
  const totAlb    = commesse.reduce((s: number, c: any) => s + parseFloat(c.totale_albergo || 0), 0)
  const numCommesse = commesse.length

  const exportBlobDownload = async (path: string, filename: string) => {
    try {
      const q: Record<string, any> = {}
      if (anno) q.anno = anno
      if (mese) q.mese = mese
      if (commessa.trim()) q.nr_commessa = commessa.trim()
      const res = await api.get(path, { params: q, responseType: 'blob' })
      const blobUrl = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      toast.error('Errore durante il download')
    }
  }

  const exportXlsx = () => exportBlobDownload(
    '/trasferte/report/export/xlsx',
    `trasferte_report_${anno || 'tutti'}.xlsx`
  )
  const exportPdf = () => exportBlobDownload(
    '/trasferte/report/export/pdf',
    `trasferte_report_${anno || 'tutti'}.pdf`
  )

  const barData = commesse.slice(0, 10).map((c: any) => ({
    name: c.nr_commessa?.length > 10 ? c.nr_commessa.slice(0, 10) + '…' : c.nr_commessa,
    diaria: parseFloat(c.totale_diaria || 0),
    albergo: parseFloat(c.totale_albergo || 0),
  }))

  const lineData = timeline.map((t: any) => ({
    name: `${MESI[t.mese]} ${t.anno}`,
    trasferte: t.num_trasferte,
    costo: t.totale_costo,
  }))

  const pad = isMobile ? '12px' : '24px'

  return (
    <div style={{ padding: pad, maxWidth: 1300 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1E40AF', margin: 0 }}>
            Dashboard Trasferte
          </h1>
          <p style={{ color: '#64748B', fontSize: 12, margin: '3px 0 0' }}>Rendicontazione e analisi delle spese trasferte</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 12px' }}
            onClick={exportXlsx}
          >
            <FileSpreadsheet size={14} />
            {isMobile ? 'XLS' : 'Excel'}
          </button>
          <button
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 12px' }}
            onClick={exportPdf}
          >
            <FileText size={14} />
            PDF
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div style={{
        background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12,
        padding: '12px 16px', marginBottom: 18,
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end',
      }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Anno</label>
          <select className="select" value={anno} onChange={e => setAnno(Number(e.target.value))} style={{ minWidth: 80 }}>
            <option value={0}>Tutti</option>
            {ANNI.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Mese</label>
          <select className="select" value={mese} onChange={e => setMese(Number(e.target.value))} style={{ minWidth: 100 }}>
            <option value={0}>Tutti</option>
            {MESI_FULL.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Commessa</label>
          <input
            type="text"
            className="input"
            placeholder="Filtra per n° commessa"
            value={commessa}
            onChange={e => setCommessa(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiCard label="Trasferte"     value={String(totTrasf)}  icon={TrendingUp} color="#1E40AF" />
        <KpiCard label="Commesse"      value={String(numCommesse)} icon={Building2} color="#7C3AED" />
        <KpiCard label="Totale Diaria" value={fmt(totDiaria)}    icon={Users}      color="#15803D" />
        <KpiCard label="Tot. Albergo"  value={fmt(totAlb)}       icon={Euro}       color="#F97316" />
        <KpiCard label="Totale Costo"  value={fmt(totCosto)}     icon={Euro}       color="#DC2626" />
      </div>

      {/* Grafici — stack su mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 14,
        marginBottom: 20,
      }}>
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1E40AF', marginBottom: 10 }}>Spese per Commessa (top 10)</div>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} margin={{ top: 4, right: 4, left: -10, bottom: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="diaria"  name="Diaria"  stackId="a" fill="#1E40AF" radius={[0, 0, 0, 0]} />
                <Bar dataKey="albergo" name="Albergo" stackId="a" fill="#F97316" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#94A3B8', fontSize: 12, textAlign: 'center', marginTop: 60 }}>
              {lcm ? 'Caricamento…' : 'Nessun dato'}
            </p>
          )}
        </div>

        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1E40AF', marginBottom: 10 }}>Trend Mensile Costi</div>
          {lineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={lineData} margin={{ top: 4, right: 4, left: -10, bottom: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="costo" name="Costo totale" stroke="#1E40AF" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#94A3B8', fontSize: 12, textAlign: 'center', marginTop: 60 }}>Nessun dato</p>
          )}
        </div>
      </div>

      {/* Tabelle accordion */}
      <AccordionTable
        titolo={`Riepilogo per Commessa (${commesse.length})`}
        intestazioni={['N° Commessa', 'Progetto/Cantiere', '# Trsf', '# Op.', 'Diaria', 'Albergo', 'Totale', 'Dal', 'Al']}
        righe={commesse.map((c: any) => [
          c.nr_commessa, c.progetto_cantiere, c.num_trasferte, c.num_operatori,
          fmt(c.totale_diaria), fmt(c.totale_albergo), fmt(c.totale_costo),
          c.prima_partenza ? new Date(c.prima_partenza).toLocaleDateString('it-IT') : '—',
          c.ultimo_rientro ? new Date(c.ultimo_rientro).toLocaleDateString('it-IT') : '—',
        ])}
      />

      <AccordionTable
        titolo={`Spese per Operatore (${operatori.length})`}
        intestazioni={['Operatore', '# Trsf', 'Giorni', 'Totale Diaria', 'Commesse']}
        righe={operatori.map((o: any) => [
          o.operatore, o.num_trasferte, o.giorni_totali,
          fmt(o.totale_diaria), Array.isArray(o.commesse) ? o.commesse.join(', ') : '—',
        ])}
      />

      {alberghi.length > 0 && (
        <AccordionTable
          titolo={`Alberghi e Strutture (${alberghi.length})`}
          intestazioni={['Nome Struttura', 'Ragione Sociale', 'IBAN', 'Destinazione', '# Sogg.', 'Totale']}
          righe={alberghi.map((a: any) => [
            a.nome_albergo || '—', a.ragione_sociale_albergo || '—',
            a.iban_albergo || '—', a.destinazione || '—',
            a.num_soggiorni, fmt(a.totale_speso),
          ])}
        />
      )}
    </div>
  )
}
